import { SMT, SMTEntryAnalyticsEvent } from "@/constants/analytics/Analytics";
import { DeepLinkParams } from "@/constants/Params";
import { SMTModalSubDeepLinks } from "@/constants/SMT";
import useSubDeepLink from "@/hooks/deepLinking/useSubDeepLink";
import useEncodedSearchParams from "@/hooks/useEncodedSearchParams";
import { useToast } from "@/hooks/useToast";
import useAppMonitor from "@/services/AppMonitor";
import { useGetSMTDailyLogEntryQuery } from "@/store/api/smt";

interface UseJournalEntrySubDeepLink {
  onOpen: (entry: SMTDailyLogEntry) => void;
}

const useJournalEntrySubDeepLink = ({ onOpen }: UseJournalEntrySubDeepLink) => {
  const { captureException } = useAppMonitor();
  const { showToast } = useToast();
  const { [DeepLinkParams.EntryId]: entryId } = useEncodedSearchParams<{
    [DeepLinkParams.EntryId]?: string;
  }>();

  const {
    data: entry,
    error,
    isLoading,
  } = useGetSMTDailyLogEntryQuery(
    { smtEntryId: entryId ?? "" },
    {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      skip: !entryId,
    },
  );

  const onOpenEntry = () => {
    if (error || !entry) {
      captureException(
        new Error(
          `Journal entry deep link with entryId ${entryId} failed to load`,
        ),
        { context: { extra: { error } } },
      );
      showToast({
        text: "Couldn’t load this journal entry",
        variant: "error",
      });
      return;
    }
    onOpen(entry);
  };

  useSubDeepLink({
    analyticsEvent: SMTEntryAnalyticsEvent.JournalEntryOpenedFromDeepLink,
    analyticsProps: {
      component: "Modal",
      smtEntryId: entryId ?? "",
      source: SMT,
    },
    deeplinkIdentifiers: [DeepLinkParams.EntryId],
    onOpen: onOpenEntry,
    paramName: "subtarget",
    shouldHandle: Boolean(entryId && !isLoading),
    targetValue: SMTModalSubDeepLinks.Entry,
  });

  return { entry };
};

export default useJournalEntrySubDeepLink;
